import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Loader2 } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { ResultOk, ResultError } from "@/components/admin/ActionCard";

/** Admin: permanently remove an accidental coin listing (needs the admin key; $SPURR is protected). */
export default function DeleteCoinCard({ adminKey }) {
  const [slug, setSlug] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const ready = slug.trim() && slug.trim().toLowerCase() === confirm.trim().toLowerCase() && adminKey.trim();

  const run = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/delete-coin`,
        { slug: slug.trim(), confirm: confirm.trim() },
        { headers: { "X-Admin-Key": adminKey.trim() } }
      );
      setResult(data);
      setSlug("");
      setConfirm("");
      toast.success(`Removed ${data.name} ($${data.ticker})`);
    } catch (e) {
      const friendly =
        e?.response?.status === 429
          ? "Too many attempts — wait a minute and try again."
          : e?.response?.data?.detail || e?.message || "Unknown error";
      setResult({ error: friendly, status: e?.response?.status });
      toast.error(friendly);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-[#161616] border border-[#6e6e6e] focus:border-[#cfff04] outline-none px-3 py-2.5 font-mono text-sm text-white placeholder:text-[#9a9a9a]";

  return (
    <div className="border border-red-400/40 bg-[#121212] p-5 mt-4" data-testid="action-delete-coin">
      <h2 className="font-display font-bold text-lg mb-1 flex items-center gap-2">
        <Trash2 size={16} className="text-red-400" /> Remove a coin
      </h2>
      <p className="text-sm text-[#e2e2e2] max-w-xl mb-4">
        Permanently deletes a listing that was created by accident — the coin page, Explore card, likes, tips,
        watchers and feed entries. Paste the admin key above first. <span className="text-[#cfff04]">$SPURR</span> can&apos;t be
        removed from here. If the coin was minted on-chain, the token itself stays on Solana.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">Coin slug (from /c/…)</span>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="e.g. solspur-db418"
            data-testid="delete-coin-slug"
            className={`${inputCls} mt-1`}
          />
        </label>
        <label className="block">
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">Type it again to confirm</span>
          <input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="same slug"
            data-testid="delete-coin-confirm"
            className={`${inputCls} mt-1`}
          />
        </label>
      </div>
      <button
        type="button"
        onClick={run}
        disabled={!ready || busy}
        data-testid="run-delete-coin"
        className="mt-4 border border-red-400 text-red-400 hover:bg-red-400 hover:text-[#050505] px-5 py-2 font-mono text-xs uppercase tracking-widest disabled:opacity-40 transition-colors flex items-center gap-2"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
        {busy ? "Removing…" : "Delete this coin forever"}
      </button>
      {!adminKey.trim() && (
        <div className="mt-2 font-mono text-[11px] text-[#d0d0d0]" data-testid="delete-coin-needs-key">
          Admin key required — paste it in the box at the top of this page.
        </div>
      )}
      <div className="mt-4">
        {result?.ok && (
          <ResultOk label="✓ REMOVED" testid="delete-coin-result-ok">
            <div className="text-sm text-[#e2e2e2]">
              <span className="text-white font-bold">{result.name}</span> (${result.ticker}, {result.status}) is gone.{" "}
              {result.note}
            </div>
            <div className="font-mono text-[11px] text-[#d0d0d0] mt-1">
              {Object.entries(result.removed || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}
            </div>
          </ResultOk>
        )}
        {result?.error && <ResultError result={result} testid="delete-coin-result-err" />}
      </div>
    </div>
  );
}
